// src/pages/ApparelPage.js
import './ApparelPage.css'; 
import React, { useState, useRef, useEffect } from 'react';
import { BACKEND_URL, supabase } from '../supabaseClient';
import ControlPanel from '../components/ControlPanel'; 
import ResultModal from '../components/ResultModal'; 
import { toast } from 'react-hot-toast';

const ApparelPage = ({ session, onJobStarted, refreshProfile }) => {
    
    // --- 1. STATE MANAGEMENT ---
    const [baseImage, setBaseImage] = useState(null);
    const [designImage, setDesignImage] = useState(null);
    const [previewBase, setPreviewBase] = useState(null);
    const [previewDesign, setPreviewDesign] = useState(null);
    
    // Config State
    const [config, setConfig] = useState({
        scale: 0.3,
        x: 0.5,
        y: 0.4, 
        rotation: 0,
        opacity: 1, 
        displacementStrength: 20, 
        claheClip: 3.0,          
        blurSize: 55,             
        shadowPower: 0.8          
    });

    const [aiLoading, setAiLoading] = useState(false);
    const [aiProgress, setAiProgress] = useState(0);

    // Result State
    const [pollingJobId, setPollingJobId] = useState(null);
    const [resultData, setResultData] = useState(null);
    const [isResultOpen, setIsResultOpen] = useState(false);

    // Canvas Refs
    const artboardRef = useRef(null); 
    const isDragging = useRef(false);
    const lastMousePos = useRef({ x: 0, y: 0 });

    // --- 2. POLLING LOGIC ---
    useEffect(() => {
        if (!pollingJobId || !session?.access_token) return;

        let isMounted = true;
        const interval = setInterval(async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/generate-mockup/status/${pollingJobId}`, {
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`
                    }
                });
                
                if (res.status === 404) {
                    clearInterval(interval);
                    if (isMounted) {
                        setAiLoading(false);
                        setPollingJobId(null);
                        toast.error("Job lost. Please try again.");
                    }
                    return;
                }

                if (res.ok) {
                    const job = await res.json();
                    
                    if (job.status === 'completed') {
                        clearInterval(interval);
                        if (!isMounted) return;
                        
                        setAiLoading(false);
                        setAiProgress(100);
                        setPollingJobId(null);

                        // Capture Data
                        const finalMockupId = job.result.mockupId;
                        setResultData({
                            url: job.result.url,
                            mockupId: finalMockupId
                        });
                        setIsResultOpen(true);
                        
                        if (refreshProfile) refreshProfile();
                        toast.success("Generation Complete!");

                        // 🟢 SILENT OPTIMIZATION TRIGGER (The Sidecar)
                        // We fire this and do NOT await it. It runs in background.
                        fetch(`${BACKEND_URL}/api/optimize-image`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${session.access_token}`
                            },
                            body: JSON.stringify({ mockupId: finalMockupId })
                        }).catch(err => console.warn("Background optimization failed:", err));

                    } 
                    else if (job.status === 'failed') {
                        clearInterval(interval);
                        if (!isMounted) return;
                        setAiLoading(false);
                        setPollingJobId(null);
                        toast.error("Job Failed: " + (job.error || "Unknown"));
                    } 
                    else {
                        if (isMounted) setAiProgress(job.progress || 50);
                    }
                }
            } catch (e) {
                console.error("Polling error", e);
            }
        }, 2000); 

        return () => { isMounted = false; clearInterval(interval); };
    }, [pollingJobId, session, refreshProfile]);

    // --- 3. UPLOAD HANDLERS ---
    const handleBaseUpload = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setBaseImage(file);
            setPreviewBase(URL.createObjectURL(file));
        }
    };

    const handleDesignUpload = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setDesignImage(file);
            setPreviewDesign(URL.createObjectURL(file));
            setConfig(prev => ({ ...prev, x: 0.5, y: 0.4, scale: 0.3, opacity: 1 }));
        }
    };

    // --- 4. DRAG & DROP LOGIC ---
    const handleMouseDown = (e) => {
        if (!designImage) return;
        e.preventDefault(); e.stopPropagation(); 
        isDragging.current = true;
        lastMousePos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current || !artboardRef.current) return;
        const deltaX = e.clientX - lastMousePos.current.x;
        const deltaY = e.clientY - lastMousePos.current.y;
        const rect = artboardRef.current.getBoundingClientRect();
        const moveX = deltaX / rect.width;
        const moveY = deltaY / rect.height;
        setConfig(prev => ({ ...prev, x: prev.x + moveX, y: prev.y + moveY }));
        lastMousePos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => { isDragging.current = false; };

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    // --- 5. GENERATE HANDLER ---
    const handleDownload = async () => {
        if (!baseImage || !designImage) { toast.error("Please upload both images first."); return; }

        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession || !currentSession.access_token) { toast.error("Please log in."); return; }
        const token = currentSession.access_token;

        setAiLoading(true);
        setAiProgress(5);

        const uploadToStorage = async (file, type) => {
            const signUrl = `${BACKEND_URL}/api/storage/sign-upload?fileName=${encodeURIComponent(file.name)}&fileType=${encodeURIComponent(file.type)}`;
            const signRes = await fetch(signUrl, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!signRes.ok) throw new Error(`Failed to sign upload for ${type}`);
            const { uploadUrl, key } = await signRes.json();
            
            // 🟢 FIX: Added Content-Type header to match Backend Signature
            const uploadRes = await fetch(uploadUrl, { 
                method: 'PUT', 
                body: file,
                headers: {
                    'Content-Type': file.type
                }
            });

            if (!uploadRes.ok) throw new Error(`Failed to upload ${type} to storage`);
            return key; 
        };

        try {
            toast.loading("Uploading images...", { id: 'upload-toast' });
            
            const [tshirtKey, designKey] = await Promise.all([
                uploadToStorage(baseImage, "T-Shirt"),
                uploadToStorage(designImage, "Design")
            ]);

            toast.dismiss('upload-toast');
            setAiProgress(30);

            const payload = { tshirtKey, designKey, config: JSON.stringify(config) };

            const response = await fetch(`${BACKEND_URL}/api/generate-mockup`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            
            const data = await response.json();

            if (response.status === 402) {
                setAiLoading(false);
                toast.error("Limit Reached: " + data.message);
                return;
            }

            if (data.success) {
                setPollingJobId(data.jobId);
                // onJobStarted call removed to prevent double modals
                toast.success("Processing started...");
            } else {
                toast.error("Failed: " + (data.error || "Unknown error"));
                setAiLoading(false);
            }
        } catch (error) {
            console.error(error);
            toast.dismiss('upload-toast');
            toast.error("Error: " + error.message);
            setAiLoading(false);
        }
    };

    // --- 6. RENDER ---
    return (
        <div className="apparel-page-container">
            
            <div className="canvas-area">
                <div ref={artboardRef} className="artboard" style={{ display: previewBase ? 'inline-block' : 'flex' }}>
                    {previewBase ? (
                        <img src={previewBase} alt="T-Shirt Base" className="base-image" />
                    ) : (
                        <div className="empty-workspace">
                            <span className="empty-title">Workspace Empty</span>
                            <span className="empty-subtitle">Upload a Product Photo to start</span>
                        </div>
                    )}

                    {previewDesign && previewBase && (
                        <div 
                            onMouseDown={handleMouseDown}
                            className="design-layer"
                            style={{
                                left: `${config.x * 100}%`,
                                top: `${config.y * 100}%`,
                                width: `${config.scale * 100}%`, 
                                transform: `translate(-50%, -50%) rotate(${config.rotation}deg)`,
                                transformOrigin: 'center center',
                                opacity: config.opacity 
                            }}
                        >
                            <img src={previewDesign} alt="Design Layer" />
                            <div className="design-handles">
                                <div className="handle-dot handle-tl"></div>
                                <div className="handle-dot handle-tr"></div>
                                <div className="handle-dot handle-bl"></div>
                                <div className="handle-dot handle-br"></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="sidebar-area">
                <ControlPanel 
                    config={config}
                    setConfig={setConfig}
                    onUploadBase={handleBaseUpload}
                    onUploadDesign={handleDesignUpload}
                    onDownload={handleDownload}
                    aiLoading={aiLoading}
                    aiProgress={aiProgress}
                    previewBase={previewBase}
                    previewDesign={previewDesign}
                />
            </div>

            <ResultModal 
                isOpen={isResultOpen}
                onClose={() => setIsResultOpen(false)}
                imageUrl={resultData?.url}
                mockupId={resultData?.mockupId} 
                session={session}
            />

        </div>
    );
};

export default ApparelPage;